import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { gql } from "graphql-tag";
import { useQuery } from "@apollo/client";
import MaterialTable, { Column } from "@material-table/core";
import { Button } from "@mui/material";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { getAllProperties, getAllProperties_properties as PropertyData } from "types/gql-types";
import { filterAdminProperties } from "./filter-admin-properties";
import PropertiesMTableToolbar from "./properties-m-table-toolbar";
import TransferPropertyModal from "./transfer-property-modal";
import type { OrgOption } from "./properties-toolbar";

const GET_PROPERTIES = gql`
  query getAllProperties {
    properties {
      id
      name
      units
      organization {
        id
        name
      }
      interestList {
        isArchived
      }
    }
  }
`;

const exportToCSV = (propertiesData?: PropertyData[]) => {
  if (!propertiesData) return;

  const csvContent = [
    ["Property Name", "Organization Name", "Units", "Status"],
    ...propertiesData.map(property => [
      property.name,
      property.organization?.name || "",
      property.units ? String(property.units) : "",
      property.interestList.isArchived ? "Archived" : "Active",
    ]),
  ].map(row => row.join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "properties-export.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const uniqueOrganizations = (properties: PropertyData[]): OrgOption[] => {
  const byId = new Map<string, string>();
  for (const p of properties) {
    const id = p.organization?.id;
    const name = p.organization?.name;
    if (id && name) byId.set(id, name);
  }
  return Array.from(byId.entries())
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([id, name]) => ({ id, name }));
};

// eslint-disable-next-line max-lines-per-function
const Properties: React.FC = () => {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = React.useState(false);
  const [organizationIdFilter, setOrganizationIdFilter] = React.useState<string | null>(null);
  const [transferProperty, setTransferProperty] = React.useState<PropertyData | null>(null);

  const { loading, error, data } =
    useQuery<getAllProperties>(GET_PROPERTIES, { fetchPolicy: "cache-and-network" });

  const organizationOptions = React.useMemo(
    () => (data?.properties ? uniqueOrganizations(data.properties) : []),
    [data?.properties],
  );

  React.useEffect(() => {
    if (organizationIdFilter &&
      !organizationOptions.some((opt) => opt.id === organizationIdFilter)) {
      setOrganizationIdFilter(null);
    }
  }, [organizationIdFilter, organizationOptions]);

  const tableRows = React.useMemo(() => {
    if (!data?.properties) {
      return [];
    }
    return filterAdminProperties(data.properties, {
      showArchived,
      organizationId: organizationIdFilter,
    });
  }, [data?.properties, showArchived, organizationIdFilter]);

  const columns = React.useMemo((): Column<PropertyData>[] => {
    const base: Column<PropertyData>[] = [
      {
        field: "name",
        title: "Property Name",
        defaultSort: "asc",
        render: ({ id, name }) => <Link to={id}>{name}</Link>,
      },
      {
        field: "organization.name",
        title: "Organization Name",
        filtering: false,
      },
      {
        field: "units",
        title: "Units",
        type: "numeric",
        emptyValue: "—",
        render: ({ units }) => (units ? units.toLocaleString() : "—"),
      },
    ];
    if (showArchived) {
      base.push({
        field: "interestList.isArchived",
        title: "Status",
        filtering: false,
        render: ({ interestList: { isArchived } }) => (isArchived ? "Archived" : "Active"),
      });
    }
    base.push({
      title: "",
      sorting: false,
      filtering: false,
      render: (property: PropertyData) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setTransferProperty(property)}
        >
          Transfer
        </Button>
      ),
    });
    return base;
  }, [showArchived]);

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <>
      <MaterialTable
        actions={[
          {
            icon: "add",
            tooltip: "Create Property",
            isFreeAction: true,
            onClick: () => navigate("/dash/admin/properties/new"),
          },
          {
            icon: "save_alt",
            tooltip: "Export to CSV",
            isFreeAction: true,
            onClick: () => exportToCSV(tableRows),
          },
        ]}
        title="Properties"
        isLoading={loading}
        data={tableRows}
        options={{
          grouping: false,
          pageSize: 30,
          pageSizeOptions: [10, 20, 30, 50],
        }}
        columns={columns}
        components={{
          Toolbar: (toolbarProps) => (
            <PropertiesMTableToolbar
              tableToolbarProps={toolbarProps}
              filterProps={{
                showArchived,
                onShowArchivedChange: setShowArchived,
                organizationId: organizationIdFilter,
                onOrganizationIdChange: setOrganizationIdFilter,
                organizationOptions,
              }}
            />
          ),
        }}
      />
      <TransferPropertyModal
        property={transferProperty}
        open={!!transferProperty}
        onClose={() => setTransferProperty(null)}
      />
    </>
  );
};

export default Properties;
